import React, {PureComponent} from "react"
import moment from "moment"

import {injectIntl, InjectIntl, FormattedMessage} from "@irati/utils/lang"
import Card from "@irati/ui/src/Layout/Card"
import theme from "@irati/ui/src/global/theme"
import Flex from "@irati/ui/src/Flex"
import {H2, Body} from "@irati/ui/src/Typography"
import {RenewalOption, RenewalQuoteStatus} from "@irati/types"
import salesService from "@irati/sales/services/sales"
import authentication from "@irati/auth"
import buService from "@irati/billing/services/bu"
import Loading from "@irati/ui/src/Loading"
import adminTracking, {RenewalPlanType, RenewalPlanPage, RenewalPlanSubSource, RenewalPlanSource} from "@irati-tracking/admin/admin"

import {CurrencyType} from "@irati/payments/enums"
import getPlan from "../RenewalQuoteSelection/getPlan"
import NewRenewalCard from "../../NewRenewalCard"
import RenewalHeader, {AddOns} from "../../Renewal/Header"
import Progress from "../../Renewal/Progress"
import {getAddOns} from "../CurrentPlan"
import ResendRenewal from "./ResendRenewal"

interface Props extends InjectIntl {
  isManual: boolean
  status: RenewalQuoteStatus
  isRenewalAccepted: boolean
  subSource: RenewalPlanSubSource
  source: RenewalPlanSource
}

interface State {
  renewalDate: string
  selectedQuote?: RenewalOption
  quotes?: RenewalOption[]
  load: Promise<any> | null
  products: AddOns[]
  accountContact?: string
  email?: string
  telephone?: string
  currencyCode?: CurrencyType
  createdAt?: number
  signedAt?: number
  sentForSignature?: number
}

class RenewalStatus extends PureComponent<Props, State> {
  state: State = {
    renewalDate: "",
    quotes: [],
    load: null,
    products: []
  }

  componentDidMount() {
    const businessId = authentication.business().uuid
    const {isRenewalAccepted, source} = this.props

    const load = Promise.all([
      buService.getCurrentPlan({businessId}),
      salesService.getRenewalQuote(businessId, {fetchPolicy: "network-only"})
    ]).then(([{endDate}, {options, products, seats, signatureContact, currency, createdAt, signedAt, sentForSignature}]) => {
      const selectedQuote = options?.[0]
      adminTracking.renewalPlan({
        source,
        stage: 4,
        subSource: isRenewalAccepted ? RenewalPlanSubSource.submitRenewal : this.props.subSource,
        page: RenewalPlanPage.renewalstatusStage,
        type: RenewalPlanType.renewalFlow,
        plan: options?.length ? getPlan(selectedQuote) : "manual"
      })
      this.setState({
        renewalDate: moment(endDate).format("DD MMM YYYY"),
        products: getAddOns(products, seats, this.props.formatMessage),
        quotes: options,
        selectedQuote,
        accountContact: `${signatureContact?.firstName} ${signatureContact?.lastName}`,
        email: signatureContact?.email,
        telephone: signatureContact?.telephone,
        currencyCode: currency,
        createdAt: createdAt?.seconds,
        signedAt: signedAt?.seconds,
        sentForSignature: sentForSignature?.seconds,
        signatureContact
      })
    })
    this.setState({load})
  }

  handleResendToNewContact = (email) => {
    this.setState({email, sentForSignature: moment().unix()})
  }

  getProgress = () => {
    const {status} = this.props
    const {email, createdAt, signedAt, sentForSignature, signatureContact} = this.state

    const signContractText = signedAt
      ? "admin.billing.renewals.progress.signContract.signedBody"
      : "admin.billing.renewals.progress.signContract.text"
    const choosePlanText = createdAt
      ? "admin.billing.renewals.progress.choosePlan.body"
      : "admin.billing.renewals.progress.choosePlan.text"
    const submitRenewalText = sentForSignature
      ? "admin.billing.renewals.progress.submitRenewal.body"
      : "admin.billing.renewals.progress.submitRenewal.text"

    const signedContractProgressStep =
      status === RenewalQuoteStatus.Completed
        ? {
            title: "admin.billing.renewals.progress.signContract.title",
            text: signContractText,
            boldText: "admin.billing.renewals.progress.signContract.signedBoldBody",
            boldValues: {date: moment.unix(signedAt).format("Do MMM")},
            completed: true
          }
        : {
            title: "admin.billing.renewals.progress.signContract.title",
            active: true
          }

    return [
      {
        title: "admin.billing.renewals.progress.choosePlan.title",
        text: choosePlanText,
        boldText: "admin.billing.renewals.progress.choosePlan.boldBody",
        boldValues: {date: moment.unix(createdAt).format("Do of MMM")},
        completed: true
      },
      {
        title: "admin.billing.renewals.progress.submitRenewal.title",
        text: submitRenewalText,
        boldText: "admin.billing.renewals.progress.submitRenewal.boldBody",
        boldValues: {email, sentForSignature: moment.unix(sentForSignature).format("Do of MMM")},
        completed: true,
        component: signatureContact && status === RenewalQuoteStatus.SentForSignature && (
          <ResendRenewal signatureContact={signatureContact} onResendToNewContact={this.handleResendToNewContact} />
        )
      },
      signedContractProgressStep
    ]
  }

  getTitleText = () => {
    const {isManual, status} = this.props
    if (status === RenewalQuoteStatus.Completed) {
      return "admin.billing.renewals.renewalCompleted.title"
    }
    if (isManual) {
      return "admin.billing.renewals.renewalManual.title"
    }
    return "admin.billing.renewals.renewalSubmitted.title"
  }

  getBodyText = () => {
    const {isManual, status} = this.props
    if (status === RenewalQuoteStatus.Completed) {
      return "admin.billing.renewals.renewalCompleted.body"
    }
    if (isManual) {
      return "admin.billing.renewals.renewalManual.body"
    }
    return "admin.billing.renewals.renewalSubmitted.body"
  }

  render() {
    const {selectedQuote, products, load, currencyCode, email} = this.state
    const {isManual, status, formatMessage} = this.props
    const titleText = this.getTitleText()
    const bodyText = this.getBodyText()
    const statusImage =
      status === RenewalQuoteStatus.Completed
        ? formatMessage({id: "admin.billing.renewals.renewalSubmitted.img2"})
        : formatMessage({id: "admin.billing.renewals.renewalSubmitted.img1"})

    return (
      <Loading load={load}>
        <Card bgColor={theme.colors.primary2} marginBottom={4} padding={5}>
          <Flex justifyContent="space-between">
            <div>
              <RenewalHeader
                title={titleText}
                body={bodyText}
                showPending={status !== RenewalQuoteStatus.Completed}
                addOns={products}
                isManual={isManual}
              />
            </div>
            {selectedQuote && !isManual && <NewRenewalCard quote={selectedQuote} hideSelector currencyCode={currencyCode} />}
          </Flex>
        </Card>
        <H2 marginBottom={1} textAlign="center">
          {status === RenewalQuoteStatus.Completed ? (
            <FormattedMessage id="admin.billing.renewals.renewalCompleted.progress.title" />
          ) : (
            <FormattedMessage id="admin.billing.renewals.renewalSubmitted.progress.title" />
          )}
        </H2>
        {status !== RenewalQuoteStatus.Completed && (
          <>
            <Body textAlign="center" maxWidth="none" color={theme.colors.greys.medium}>
              <FormattedMessage id="admin.billing.renewals.renewalSubmitted.progress.text1" values={{email}} />
            </Body>
            <Body marginBottom={3} textAlign="center" maxWidth="none" color={theme.colors.greys.medium}>
              <FormattedMessage id="admin.billing.renewals.renewalSubmitted.progress.text2" />
            </Body>
          </>
        )}
        <Flex justifyContent="center" marginBottom={5}>
          <img src={statusImage} />
        </Flex>

        <Progress progress={this.getProgress()} />
      </Loading>
    )
  }
}

export default injectIntl(RenewalStatus)
